import {Box, Button, Chip, Collapse, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Tooltip, IconButton} from "@mui/material";
import {FitnessCenter as WorkoutIcon, Add as AddIcon, Sync as SyncIcon, Delete as DeleteIcon, Settings as SettingsIcon, ExpandMore as ExpandMoreIcon, History as HistoryIcon, LocalFireDepartment as WarmupIcon, DateRange as DateRangeIcon} from "@mui/icons-material";
import Block from "./Block";
import {useEffect, useState} from "react";
import {useNavigate} from "react-router-dom";
import {addNewBlock, getCurrentPlan, changeProgram, deleteBlock, appendWeekToBlock} from "../services/PlanSerice";
import {getTraineeProfile} from "../services/CoachDashboardService";
import {getErrorMessage} from "../enums/ErrorMessages";
import {useMessageContext} from "../contexts/MessageContext";
import {getClient} from "../services/TraineeService";
import Loader from "./Loader";
import ChartCard from "./client/ChartCard";
import ChangeProgramDialog from "./ChnageProgramDialog";
import ProgramSettingsDialog from "./client/ProgramSettingsDialog";
import ProgramWarmupDialog from "./client/ProgramWarmupDialog";
import {RTL} from "../cache/CacheRtl";
import {colors, borders} from "./ui/colors";
import {t as coachT} from "./dashboard/coach/coachTokens";

const ClientBlocks = ({clientId, cardVariant = "default"}) => {
    const coach = cardVariant === "coach";
    const navigate = useNavigate();
    const [program, setProgram] = useState([]);
    const {notificationHandler} = useMessageContext();
    const [refetch, setRefetch] = useState(false);
    const [loading, setLoading] = useState(false);
    const [changeProgramDialogOpen, setChangeProgramDialogOpen] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [settingsDialogOpen, setSettingsDialogOpen] = useState(false);
    const [warmupDialogOpen, setWarmupDialogOpen] = useState(false);
    const [client, setClient] = useState(null);
    const [profile, setProfile] = useState(null);          // coach: current-week workout detail
    const [historyOpen, setHistoryOpen] = useState(false); // coach: collapsible history

    useEffect(() => {
        getCurrentPlan(clientId).then(res => {
            setProgram(res.data);
        }).catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)))
    }, [refetch])

    useEffect(() => {
        if (!coach) return;
        getTraineeProfile(clientId).then(r => setProfile(r.data || null)).catch(() => setProfile(null));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [refetch])

    const MAX_NOT_STARTED_BLOCKS = 3;

    const countNotStartedBlocks = () => {
        if (!program?.blocks) return 0;
        return program.blocks.filter(block =>
            block?.weeks?.every(week => !week.finished)
        ).length;
    };

    const handleNewBlock = () => {
        if (!program?.id) {
            notificationHandler.error("אין תוכנית פעילה");
            return;
        }
        if (countNotStartedBlocks() >= MAX_NOT_STARTED_BLOCKS) {
            notificationHandler.error("לא ניתן ליצור יותר מ-3 בלוקים שלא התחילו");
            return;
        }
        setLoading(true)
        addNewBlock(program.id)
            .then(() => setRefetch(prv => !prv))
            .then(() => setLoading(false))
            .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)))
            .catch(() => setLoading(false))
    }

    /**
     * Append a week to the block the trainee is currently in.
     *
     * The server refuses a finished block, and refuses outright when a week the trainee has
     * already completed would be re-typed (the ladder pins PEAK/DELOAD from the end, so growing a
     * block moves them). Both come back as a 400 whose message names the week, so it is surfaced
     * verbatim rather than mapped to a generic error.
     */
    const handleAddWeek = () => {
        const target = [...(program?.blocks || [])]
            .sort((a, b) => a.index - b.index)
            .find(b => !b.finished);
        if (!target) {
            notificationHandler.error("אין בלוק פעיל להוספת שבוע");
            return;
        }
        setLoading(true);
        appendWeekToBlock(target.id)
            .then(({data}) => {
                const grown = data?.blocks || [];
                const weeks = grown[0]?.totalWeeksInBlock ?? "?";
                // The append cascades to later blocks so the trainee's cycle does not shorten when
                // they roll into the next one — say so when more than one block changed.
                notificationHandler.success(grown.length > 1
                    ? `שבוע נוסף ל-${grown.length} בלוקים (סה"כ ${weeks} שבועות בכל בלוק)`
                    : `שבוע נוסף (סה"כ ${weeks} שבועות בבלוק)`);
            })
            .then(() => setRefetch(prv => !prv))
            .catch(e => notificationHandler.error(
                e.response?.data?.message || getErrorMessage(e.response?.data?.errorCode)))
            .finally(() => setLoading(false));
    }

    const handleOpenChangeProgramDialog = () => {
        setLoading(true);
        getClient(clientId)
            .then(res => {
                setClient(res.data);
                setChangeProgramDialogOpen(true);
                setLoading(false);
            })
            .catch(e => {
                notificationHandler.error(getErrorMessage(e.response.data?.errorCode));
                setLoading(false);
            });
    }

    const handleOpenSettingsDialog = () => {
        if (client) {
            setSettingsDialogOpen(true);
        } else {
            setLoading(true);
            getClient(clientId)
                .then(res => {
                    setClient(res.data);
                    setSettingsDialogOpen(true);
                    setLoading(false);
                })
                .catch(e => {
                    notificationHandler.error(getErrorMessage(e.response.data?.errorCode));
                    setLoading(false);
                });
        }
    }

    const handleChangeProgram = (templateId, includeRmWeek = false, advancedSettings = null) => {
        changeProgram(clientId, templateId, includeRmWeek, advancedSettings)
            .then(() => notificationHandler.success("תוכנית שונתה בהצלחה"))
            .then(() => setChangeProgramDialogOpen(false))
            .then(() => setRefetch(prv => !prv))
            .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)));
    }

    const handleDeleteBlockClick = () => {
        setDeleteDialogOpen(true);
    }

    const handleDeleteBlockConfirm = () => {
        const sortedBlocks = [...(program?.blocks || [])].sort((a, b) => b.index - a.index);
        const lastBlock = sortedBlocks[0];
        if (!lastBlock) return;

        setDeleteDialogOpen(false);
        setLoading(true);
        deleteBlock(lastBlock.id)
            .then(() => notificationHandler.success("בלוק נמחק בהצלחה"))
            .then(() => setRefetch(prv => !prv))
            .catch(e => notificationHandler.error(getErrorMessage(e.response.data?.errorCode)))
            .finally(() => setLoading(false));
    }

    const canDeleteLastBlock = () => {
        if (!program?.blocks || program.blocks.length <= 1) return false;
        const sortedBlocks = [...program.blocks].sort((a, b) => b.index - a.index);
        const lastBlock = sortedBlocks[0];
        return lastBlock?.weeks?.every(week => !week.finished) ?? false;
    }

    // ── Blocks + current week (coach) ──────────
    const blocksAsc = [...(program?.blocks || [])].sort((a, b) => a.index - b.index);

    // First not-finished week, scanning blocks/weeks in order
    const findCurrentWeek = () => {
        for (const block of blocksAsc) {
            const weeks = [...(block.weeks || [])].sort((a, b) => a.index - b.index);
            const wi = weeks.findIndex(w => !w.finished);
            if (wi !== -1) return {blockIndex: block.index, weekIndex: wi};
        }
        return null;
    };

    const openCurrentWeek = () => {
        const cw = findCurrentWeek();
        if (!program?.id || !cw) {
            notificationHandler.error("אין שבוע פעיל לפתיחה");
            return;
        }
        navigate(`/app/clients/${clientId}/${program.id}/week/${cw.blockIndex}/${cw.weekIndex}`);
    };

    return (
        <>
            <Loader loading={loading}/>

            {client && (
                <ChangeProgramDialog
                    trainee={client}
                    handleChangeProgram={handleChangeProgram}
                    isOpen={changeProgramDialogOpen}
                    close={() => setChangeProgramDialogOpen(false)}
                />
            )}

            {client && program?.id && (
                <ProgramSettingsDialog
                    isOpen={settingsDialogOpen}
                    close={() => setSettingsDialogOpen(false)}
                    client={client}
                    program={program}
                    handleReload={() => setRefetch(prv => !prv)}
                />
            )}

            {program?.id && (
                <ProgramWarmupDialog
                    isOpen={warmupDialogOpen}
                    close={() => setWarmupDialogOpen(false)}
                    program={program}
                    handleReload={() => setRefetch(prv => !prv)}
                />
            )}

            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                dir="rtl"
            >
                <DialogTitle>מחיקת בלוק</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        האם אתה בטוח שברצונך למחוק את הבלוק האחרון?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)}>ביטול</Button>
                    <Button onClick={handleDeleteBlockConfirm} color="error" autoFocus>
                        מחק
                    </Button>
                </DialogActions>
            </Dialog>

            <ChartCard
                variant={cardVariant}
                title="תוכניות אימון"
                subtitle={cardVariant === "coach" ? "בלוקים ושבועות אימון" : undefined}
                icon={WorkoutIcon}
                loading={false}
                showData={program && program.blocks?.length > 0}
                emptyMessage="לא הוקצתה תוכנית אימון למתאמן. לחץ על 'שינוי תוכנית' כדי להקצות תוכנית."
                flexBasis="50%"
                contentPadding="0"
                actionButton={
                    coach ? (
                        /* Coach: secondary controls — flags + icon actions (mirrors מעקב מדידות גוף) */
                        program?.id ? (
                            <Box sx={{display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap"}}>
                                <CoachFlag on={program.automaticProgression} onLabel="אוטומטי" offLabel="ידני" tooltip={program.automaticProgression ? "התקדמות אוטומטית" : "התקדמות ידנית"}/>
                                <CoachFlag on={program.useProgressionAlgorithm} onLabel="אלגוריתם" offLabel="ללא אלגוריתם" tone="blue" tooltip={program.useProgressionAlgorithm ? "אלגוריתם התקדמות" : "ללא אלגוריתם התקדמות"}/>
                                <Tooltip title="שינוי תוכנית">
                                    <IconButton size="small" onClick={handleOpenChangeProgramDialog}
                                        sx={{width: 28, height: 28, color: coachT.t2, "&:hover": {background: coachT.greenLight, color: coachT.green}}}>
                                        <SyncIcon sx={{fontSize: 17}}/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="הוספת בלוק">
                                    <span>
                                        <IconButton size="small" onClick={handleNewBlock}
                                            disabled={!program?.id || countNotStartedBlocks() >= MAX_NOT_STARTED_BLOCKS}
                                            sx={{width: 28, height: 28, color: coachT.t2, "&:hover": {background: coachT.greenLight, color: coachT.green}}}>
                                            <AddIcon sx={{fontSize: 18}}/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                <Tooltip title="הוספת שבוע לבלוק הנוכחי">
                                    <span>
                                        <IconButton size="small" onClick={handleAddWeek}
                                            disabled={!program?.id || !(program?.blocks || []).some(b => !b.finished)}
                                            sx={{width: 28, height: 28, color: coachT.t2, "&:hover": {background: coachT.greenLight, color: coachT.green}}}>
                                            <DateRangeIcon sx={{fontSize: 16}}/>
                                        </IconButton>
                                    </span>
                                </Tooltip>
                                {canDeleteLastBlock() && (
                                    <Tooltip title="מחיקת בלוק">
                                        <IconButton size="small" onClick={handleDeleteBlockClick}
                                            sx={{width: 28, height: 28, color: coachT.t2, "&:hover": {background: coachT.redLight, color: coachT.red}}}>
                                            <DeleteIcon sx={{fontSize: 16}}/>
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip title="הגדרות תוכנית">
                                    <IconButton size="small" onClick={handleOpenSettingsDialog}
                                        sx={{width: 28, height: 28, color: coachT.t2, "&:hover": {background: coachT.greenLight, color: coachT.green}}}>
                                        <SettingsIcon sx={{fontSize: 16}}/>
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="טקסט חימום">
                                    <IconButton size="small" onClick={() => setWarmupDialogOpen(true)}
                                        sx={{width: 28, height: 28, color: coachT.t2, "&:hover": {background: coachT.greenLight, color: coachT.green}}}>
                                        <WarmupIcon sx={{fontSize: 16}}/>
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        ) : (
                            /* No program yet — offer to assign one (was missing in coach v2) */
                            <Box component="button" onClick={handleOpenChangeProgramDialog} sx={{
                                display: "inline-flex", alignItems: "center", gap: "5px",
                                background: coachT.green, color: "#fff", height: 32, px: "14px", borderRadius: coachT.rs,
                                fontSize: "12.5px", fontWeight: 600, border: "none", cursor: "pointer", fontFamily: "inherit",
                                transition: "all .14s",
                                "&:hover": {background: coachT.greenDark, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(1,187,167,.35)"},
                            }}>
                                <SyncIcon sx={{fontSize: 16}}/>
                                שייך תוכנית
                            </Box>
                        )
                    ) : (
                    <Box sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 1.5,
                        alignItems: { xs: "flex-start", md: "flex-end" }
                    }}>
                        {/* Program Settings Info */}
                        {program?.id && (
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                flexWrap: "wrap"
                            }}>
                                <Tooltip title="התקדמות אוטומטית - כאשר מופעל, שבוע חדש נפתח אוטומטית. כאשר כבוי, יש לאשר ידנית דרך עמוד עדכון תוכנית">
                                    <Chip
                                        size="small"
                                        label={program.automaticProgression ? "התקדמות אוטומטית" : "התקדמות ידנית"}
                                        sx={{
                                            fontSize: "11px",
                                            height: "24px",
                                            backgroundColor: coach
                                                ? (program.automaticProgression ? coachT.greenLight : coachT.amberLight)
                                                : (program.automaticProgression ? colors.successBg : colors.warningBg),
                                            color: coach
                                                ? (program.automaticProgression ? coachT.greenText : coachT.amberText)
                                                : (program.automaticProgression ? colors.successText : "#92400E"),
                                            fontWeight: 500
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title="אלגוריתם התקדמות - כאשר מופעל, המערכת מתקדמת לפי רמות מאמץ. כאשר כבוי, ההמלצה לשבוע הבא היא המשקל והחזרות שבוצעו בפועל בשבוע הקודם">
                                    <Chip
                                        size="small"
                                        label={program.useProgressionAlgorithm ? "אלגוריתם התקדמות" : "ללא אלגוריתם התקדמות"}
                                        sx={{
                                            fontSize: "11px",
                                            height: "24px",
                                            backgroundColor: coach
                                                ? (program.useProgressionAlgorithm ? coachT.blueLight : coachT.bg)
                                                : (program.useProgressionAlgorithm ? colors.infoBg : colors.neutralBg),
                                            color: coach
                                                ? (program.useProgressionAlgorithm ? coachT.blueText : coachT.t2)
                                                : (program.useProgressionAlgorithm ? colors.infoText : colors.neutral),
                                            fontWeight: 500
                                        }}
                                    />
                                </Tooltip>
                                <Tooltip title="הגדרות תוכנית">
                                    <IconButton
                                        size="small"
                                        onClick={handleOpenSettingsDialog}
                                        sx={{
                                            backgroundColor: colors.neutralBg,
                                            width: "24px",
                                            height: "24px",
                                            "&:hover": {
                                                backgroundColor: colors.bgHover
                                            }
                                        }}
                                    >
                                        <SettingsIcon sx={{ fontSize: "14px", color: colors.neutral }} />
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="טקסט חימום">
                                    <IconButton
                                        size="small"
                                        onClick={() => setWarmupDialogOpen(true)}
                                        sx={{
                                            backgroundColor: colors.neutralBg,
                                            width: "24px",
                                            height: "24px",
                                            "&:hover": {
                                                backgroundColor: colors.bgHover
                                            }
                                        }}
                                    >
                                        <WarmupIcon sx={{ fontSize: "14px", color: colors.neutral }} />
                                    </IconButton>
                                </Tooltip>
                            </Box>
                        )}

                        {/* Action Buttons */}
                        <Box sx={{
                            display: "flex",
                            flexWrap: "wrap",
                            gap: 1
                        }}>
                        <RTL>
                        <Button
                            variant="outlined"
                            onClick={handleOpenChangeProgramDialog}
                            startIcon={<SyncIcon/>}
                            sx={{
                                backgroundColor: colors.white,
                                color: coach ? coachT.text : colors.textPrimary,
                                fontSize: { xs: "12px", md: "14px" },
                                fontWeight: 500,
                                padding: { xs: "4px 10px", md: "6px 15px" },
                                textTransform: "none",
                                borderRadius: coach ? coachT.rs : borders.radius.md,
                                border: coach ? `1px solid ${coachT.border}` : borders.default,
                                boxShadow: "none",
                                height: { xs: "28px", md: "32px" },
                                "&:hover": {
                                    backgroundColor: coach ? coachT.greenLight : colors.bg,
                                    border: coach ? `1px solid ${coachT.green}` : borders.hover,
                                    color: coach ? coachT.green : undefined,
                                    boxShadow: "none"
                                }
                            }}
                        >
                            שינוי תוכנית
                        </Button>
                        </RTL>
                        <RTL>
                        <Button
                            variant="contained"
                            onClick={handleNewBlock}
                            disabled={!program?.id || countNotStartedBlocks() >= MAX_NOT_STARTED_BLOCKS}
                            startIcon={<AddIcon/>}
                            sx={{
                                backgroundColor: colors.white,
                                color: coach ? coachT.text : colors.textPrimary,
                                fontSize: { xs: "12px", md: "14px" },
                                fontWeight: 500,
                                padding: { xs: "4px 10px", md: "6px 15px" },
                                textTransform: "none",
                                borderRadius: coach ? coachT.rs : borders.radius.md,
                                border: coach ? `1px solid ${coachT.border}` : borders.default,
                                boxShadow: "none",
                                height: { xs: "28px", md: "32px" },
                                "&:hover": {
                                    backgroundColor: coach ? coachT.greenLight : colors.bg,
                                    border: coach ? `1px solid ${coachT.green}` : borders.hover,
                                    color: coach ? coachT.green : undefined,
                                    boxShadow: "none"
                                },
                                "&:disabled": {
                                    backgroundColor: colors.bgHover,
                                    color: colors.textMuted,
                                    border: borders.default
                                }
                            }}
                        >
                            הוספת בלוק
                        </Button>
                        </RTL>
                        {canDeleteLastBlock() && (
                            <RTL>
                            <Button
                                variant="outlined"
                                onClick={handleDeleteBlockClick}
                                startIcon={<DeleteIcon/>}
                                sx={{
                                    backgroundColor: colors.white,
                                    color: colors.error,
                                    fontSize: { xs: "12px", md: "14px" },
                                    fontWeight: 500,
                                    padding: { xs: "4px 10px", md: "6px 15px" },
                                    textTransform: "none",
                                    borderRadius: borders.radius.md,
                                    border: `1px solid ${colors.error}`,
                                    boxShadow: "none",
                                    height: { xs: "28px", md: "32px" },
                                    "&:hover": {
                                        backgroundColor: colors.errorBg,
                                        border: `1px solid ${colors.error}`,
                                        boxShadow: "none"
                                    }
                                }}
                            >
                                מחיקת בלוק
                            </Button>
                            </RTL>
                        )}
                        </Box>
                    </Box>
                    )
                }
                headerAction={
                    coach && program?.id && findCurrentWeek() ? (
                        <Box component="button" onClick={openCurrentWeek} sx={{
                            display: "inline-flex", alignItems: "center", whiteSpace: "nowrap",
                            background: coachT.green, color: "#fff",
                            height: 32, px: "14px", borderRadius: coachT.rs,
                            fontSize: "12.5px", fontWeight: 700,
                            border: "none", cursor: "pointer", fontFamily: "inherit",
                            boxShadow: "0 3px 10px rgba(1,187,167,.3)",
                            transition: "all .14s",
                            "&:hover": {background: coachT.greenDark, transform: "translateY(-1px)"},
                        }}>
                            פתח שבוע נוכחי
                        </Box>
                    ) : null
                }
            >
                {coach ? (
                    <Box sx={{display: "flex", flexDirection: "column", gap: 1.5, padding: "16px"}}>
                        {/* ── Area 1: Current week (dashboard-style) ── */}
                        <CurrentWeekPanel profile={profile}/>

                        {/* ── Area 2: Blocks — last block open, older behind "show more" ── */}
                        {blocksAsc.length > 0 && (() => {
                            const ordered = blocksAsc.slice().reverse();   // newest (last) first
                            // The program-wide current week (first unfinished week, scanning blocks in order).
                            // Only this block should highlight a "current week" pill — not every not-started block.
                            const currentWeek = findCurrentWeek();
                            // Pin the block that holds the current week so it stays visible while the rest
                            // are collapsed. If the whole program is finished, fall back to the last block.
                            const pinnedBlock =
                                (currentWeek && ordered.find(b => b.index === currentWeek.blockIndex)) || ordered[0];
                            const olderBlocks = ordered.filter(b => b.index !== pinnedBlock.index);
                            const openWeek = (block) => (weekIndex) => navigate(`/app/clients/${clientId}/${program.id}/week/${block.index}/${weekIndex}`);
                            const renderBlock = (block) => (
                                <CoachBlock
                                    key={block.index}
                                    block={block}
                                    index={block.index + 1}
                                    currentWeek={currentWeek}
                                    onOpenWeek={openWeek(block)}
                                />
                            );
                            return (
                                <Box sx={{borderTop: `1px solid ${coachT.border}`, paddingTop: "12px", display: "flex", flexDirection: "column", gap: 1.25}}>
                                    {/* Collapsed: pin only the current-week block. Expanded: all blocks render
                                        below in their natural order, so the pinned copy is hidden to avoid a duplicate. */}
                                    {!historyOpen && renderBlock(pinnedBlock)}

                                    {/* Older blocks — show more */}
                                    {olderBlocks.length > 0 && (
                                        <>
                                            <Collapse in={historyOpen}>
                                                <Box sx={{display: "flex", flexDirection: "column", gap: 1.25}}>
                                                    {ordered.map(renderBlock)}
                                                </Box>
                                            </Collapse>
                                            <Box component="button" onClick={() => setHistoryOpen(o => !o)} sx={{
                                                display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "5px",
                                                alignSelf: "center", background: "transparent", border: "none", cursor: "pointer", fontFamily: "inherit",
                                                fontSize: 12.5, fontWeight: 700, color: coachT.green, padding: "4px 10px",
                                                "&:hover": {color: coachT.greenDark},
                                            }}>
                                                <HistoryIcon sx={{fontSize: 16}}/>
                                                {historyOpen ? "הסתר בלוקים קודמים" : `הצג ${olderBlocks.length} בלוקים קודמים`}
                                                <ExpandMoreIcon sx={{fontSize: 18, transform: historyOpen ? "rotate(180deg)" : "none", transition: "transform .18s"}}/>
                                            </Box>
                                        </>
                                    )}
                                </Box>
                            );
                        })()}
                    </Box>
                ) : (
                <Box
                    sx={{
                        maxHeight: "400px",
                        overflowY: "auto",
                        "&::-webkit-scrollbar": {
                            width: "8px"
                        },
                        "&::-webkit-scrollbar-track": {
                            backgroundColor: colors.neutralBg,
                            borderRadius: "4px"
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: colors.borderHover,
                            borderRadius: "4px",
                            "&:hover": {
                                backgroundColor: colors.textMuted
                            }
                        }
                    }}
                >
                    <Box sx={{display: "flex", flexDirection: "column", gap: 2, padding: "20px"}}>
                        {program && program.blocks?.sort((a, b) => b.index > a.index ? -1 : 1)
                            .map((block, index) => (
                                <Block
                                    programId={program?.id}
                                    key={index}
                                    block={block}
                                    index={block.index + 1}
                                />
                            ))}
                    </Box>
                </Box>
                )}
            </ChartCard>
        </>
    );
};

/* ── Current-week panel (dashboard profile style) ────────── */
const DAY_HEADERS = ["א׳", "ב׳", "ג׳", "ד׳", "ה׳", "ו׳", "ש׳"];

const CurrentWeekPanel = ({profile}) => {
    const done = profile?.workoutsDone || 0;
    const goal = profile?.workoutsGoal || 0;
    const metGoal = goal > 0 && done >= goal;
    const todayDow = new Date().getDay(); // 0=Sun .. 6=Sat, matches the strip index

    return (
        <Box sx={{
            background: coachT.bg,
            border: `1px solid ${coachT.border}`, borderRadius: coachT.rs,
            padding: "14px 16px",
        }}>
            {/* Title row */}
            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1.25, flexWrap: "wrap"}}>
                <Box sx={{fontSize: 13.5, fontWeight: 800, color: coachT.text}}>השבוע הנוכחי</Box>
                {profile?.blockTotal > 0 && (
                    <Box sx={{fontSize: 11, fontWeight: 700, color: coachT.greenText, background: coachT.greenLight, padding: "2px 9px", borderRadius: "20px"}}>
                        שבוע {profile.blockWeek}/{profile.blockTotal}
                    </Box>
                )}
                {profile?.planName && (
                    <Box sx={{fontSize: 11.5, color: coachT.t2, fontWeight: 500, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}}>
                        {profile.planName}
                    </Box>
                )}
            </Box>

            {/* 7-day workout strip */}
            <Box sx={{display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: "5px", mb: 1}}>
                {DAY_HEADERS.map((label, i) => {
                    const state = (profile?.weekDays || [])[i];
                    const isDone = state === "COMPLETED";
                    const inProgress = state === "IN_PROGRESS";
                    const count = (profile?.weekDayCounts || [])[i] || 0;
                    const isToday = i === todayDow;
                    return (
                        <Box key={label} sx={{textAlign: "center"}}>
                            <Box sx={{fontSize: 10, fontWeight: isToday ? 800 : 400, color: isToday ? coachT.green : coachT.t3, mb: "3px"}}>
                                {isToday ? "היום" : label}
                            </Box>
                            <Box sx={{
                                position: "relative", width: 28, height: 28, borderRadius: "50%", margin: "0 auto",
                                display: "flex", alignItems: "center", justifyContent: "center",
                                fontSize: 12, fontWeight: 700,
                                background: isDone ? coachT.green : coachT.white,
                                color: isDone ? "#fff" : inProgress ? coachT.text : coachT.t3,
                                border: isToday
                                    ? `2px solid ${coachT.green}`
                                    : `1px solid ${isDone ? coachT.green : coachT.border}`,
                                boxShadow: isToday ? `0 0 0 3px ${coachT.greenLight}` : "none",
                            }}>
                                {isDone ? "✓" : inProgress ? "⋯" : "–"}
                                {count > 1 && (
                                    <Box sx={{position: "absolute", top: -4, insetInlineEnd: -4, minWidth: 14, height: 14, px: "2px", borderRadius: "7px", background: coachT.greenText, color: "#fff", fontSize: 9, fontWeight: 700, lineHeight: "14px"}}>
                                        ×{count}
                                    </Box>
                                )}
                            </Box>
                        </Box>
                    );
                })}
            </Box>

            {/* Summary line */}
            <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                <Box sx={{fontSize: 13, fontWeight: 800, color: metGoal ? coachT.green : coachT.text}}>
                    {goal > 0 ? `${done}/${goal} אימונים` : `${done} אימונים`}
                </Box>
                {profile?.lastWorkoutDate && (
                    <Box sx={{fontSize: 11, color: coachT.t3}}>· אחרון: {lastWorkoutText(profile.lastWorkoutDate)}</Box>
                )}
                {!profile && (
                    <Box sx={{fontSize: 11.5, color: coachT.t3}}>אין נתוני אימון לשבוע זה</Box>
                )}
            </Box>
        </Box>
    );
};

export const lastWorkoutText = (instantOrSecs) => {
    if (instantOrSecs == null) return "—";
    const secs = instantOrSecs > 1e12 ? Math.floor(instantOrSecs / 1000) : instantOrSecs;
    // Compare LOCAL midnights. The previous `Math.floor(secs / 86400)` bucketed by UTC day, so a
    // workout finished at 01:00 in Israel fell into the previous bucket and read "אתמול" on the
    // day it happened. WorkoutsTab and TraineeProfileDialog already normalize to local midnight;
    // this third copy did not.
    const dayStart = new Date(secs * 1000);
    dayStart.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const days = Math.round((today - dayStart) / 86400000);
    if (days <= 0) return "היום";
    if (days === 1) return "אתמול";
    if (days <= 30) return `לפני ${days} ימים`;
    return `לפני ${Math.floor(days / 30)} חודשים`;
};

/* ── Coach-styled block card (replaces old Block in v2) ───── */
const WEEK_TYPE_SHORT = {
    RM_WEEK: "RM", FIRST_WEEK: "ראשון", LOAD_WEEK: "עומס",
    LOAD_PLUS: "עומס", LOAD_PLUS_PLUS: "עומס", LOAD_PLUS_PLUS_PLUS: "עומס",
    PRE_PEAK_WEEK: "טרום-שיא", PEAK_WEEK: "שיא", DELOAD_WEEK: "הורדת עומסים",
};

const CoachBlock = ({block, index, currentWeek, onOpenWeek}) => {
    const weeks = [...(block.weeks || [])].sort((a, b) => a.index - b.index);
    const done = weeks.filter(w => w.finished).length;
    const total = weeks.length;
    const allDone = total > 0 && done === total;
    // Highlight the "current week" pill only on the program's active block — the one
    // holding the program-wide current week. Other blocks (incl. not-started ones) show none.
    const currentIdx = currentWeek && currentWeek.blockIndex === block.index ? currentWeek.weekIndex : -1;

    return (
        <Box sx={{
            background: coachT.white,
            border: `1px solid ${allDone ? "rgba(1,187,167,.3)" : coachT.border}`,
            borderRadius: coachT.rs,
            padding: "12px 14px",
        }}>
            {/* Block header */}
            <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1}}>
                <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                    <Box sx={{fontSize: 13.5, fontWeight: 700, color: coachT.text}}>בלוק {index}</Box>
                    <Box sx={{fontSize: 11.5, color: coachT.t3, fontWeight: 600}}>{total} שבועות</Box>
                </Box>
                <Box sx={{
                    fontSize: 10.5, fontWeight: 700, padding: "2px 9px", borderRadius: "20px",
                    background: allDone ? coachT.greenLight : (done > 0 ? coachT.amberLight : coachT.bg),
                    color: allDone ? coachT.greenText : (done > 0 ? coachT.amberText : coachT.t3),
                    whiteSpace: "nowrap",
                }}>
                    {allDone ? "✓ הושלם" : done > 0 ? `${done}/${total} הושלמו` : "לא התחיל"}
                </Box>
            </Box>

            {/* Week pills */}
            <Box sx={{display: "flex", flexDirection: "row-reverse", gap: "5px", flexWrap: "wrap"}}>
                {weeks.map((week, wi) => {
                    const isCurrent = wi === currentIdx;
                    const typeLabel = week.weekType ? WEEK_TYPE_SHORT[week.weekType] : null;
                    const statusLabel = isCurrent ? "שבוע נוכחי" : week.finished ? "הושלם" : "טרם החל";
                    return (
                        <Tooltip key={wi} title={typeLabel ? `${typeLabel} · ${statusLabel}` : statusLabel} arrow>
                            <Box
                                onClick={() => onOpenWeek(wi)}
                                sx={{
                                    flex: "1 1 auto", minWidth: 30, height: 30, borderRadius: coachT.rxs,
                                    display: "flex", alignItems: "center", justifyContent: "center",
                                    cursor: "pointer", fontSize: 11.5, fontWeight: 700, fontFamily: "inherit",
                                    background: week.finished ? coachT.green : (isCurrent ? coachT.white : coachT.bg),
                                    color: week.finished ? "#fff" : (isCurrent ? coachT.green : coachT.t2),
                                    border: isCurrent ? `1.5px solid ${coachT.green}` : `1px solid ${coachT.border}`,
                                    boxShadow: isCurrent ? "0 2px 8px rgba(1,187,167,.25)" : "none",
                                    transition: "all .14s",
                                    "&:hover": {transform: "translateY(-2px)", borderColor: coachT.green},
                                }}
                            >
                                {wi + 1}
                            </Box>
                        </Tooltip>
                    );
                })}
            </Box>
        </Box>
    );
};

const CoachFlag = ({on, onLabel, offLabel, tone = "green", tooltip}) => {
    const onBg = tone === "blue" ? coachT.blueLight : coachT.greenLight;
    const onColor = tone === "blue" ? coachT.blueText : coachT.greenText;
    const chip = (
        <Box sx={{
            fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: "20px", whiteSpace: "nowrap",
            background: on ? onBg : coachT.bg,
            color: on ? onColor : coachT.t3,
            border: on ? "1px solid transparent" : `1px dashed ${coachT.border}`,
        }}>
            {on ? onLabel : offLabel}
        </Box>
    );
    return tooltip ? <Tooltip title={tooltip}>{chip}</Tooltip> : chip;
};

export default ClientBlocks;